import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { getSnapshots, getSnapshotOptions, getFilingsInsights, getFilingsScopeConfig, getEvents } from '../../../api';
import {
  changeEventDotClass,
  formatChangeEventDetail,
  formatChangeEventType,
} from '../../../utils/changeEvents';
import { format } from 'date-fns';
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import FilingVolumeChart from '../../../components/charts/FilingVolumeChart';
import OutcomeDistributionChart from '../../../components/charts/OutcomeDistributionChart';
import CompetitorVolumeChart from '../../../components/charts/CompetitorVolumeChart';
import CompetitorCadenceHeatmap from '../../../components/charts/CompetitorCadenceHeatmap';
import ProductCategoryDepthChart from '../../../components/charts/ProductCategoryDepthChart';
import FiltersDrawer from '../../../components/FiltersDrawer';
import {
  assignFilterArray,
  parseMultiSelect,
  resolveActiveCompetitorNames,
  serializeMultiSelect,
  toApiFilterArray,
} from '../../../utils/filtersApi';
import PageHeader from '../../../components/PageHeader';
import CompanyBrandCell from '../../../components/CompanyBrandCell';
import KPICard from '../../../components/KPICard';
import { mapInsightsKpiToCardProps } from '../../../utils/mapInsightsKpiCard';
import {
  getPbBarStyle,
  CANONICAL_SPECTRUM as MASTER_SPECTRUM,
  getFilingVolumeTypeColor,
  COMPETITOR_CADENCE_COLOR,
} from '../../../utils/chartUtils';

// Foolproof dynamic index safety wrap guarantees that legacy hard-coded high indices (e.g., [25]) 
// automatically wrap gracefully around our refined 19-entry high-fidelity authorized array limit.
const CANONICAL_SPECTRUM = new Proxy(MASTER_SPECTRUM, {
  get: (target, name) => {
    if (typeof name === 'string' && !isNaN(name)) {
      return target[parseInt(name, 10) % target.length];
    }
    return target[name];
  }
});

const STATE_ABBREVIATIONS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH',
  Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',   Wyoming: 'WY'
};

const GEO_MAP_TARGET_HEIGHT = 400;
const GEO_RANKING_HEADER_PX = 70;
const GEO_RANKING_BAR_HEIGHT = 8;
const GEO_RANKING_GAP_MIN = 6;
const GEO_RANKING_GAP_MAX = 28;
const GEO_RANKING_CHART_PADDING_BOTTOM = 16;

const Filings = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const formatValue = (val, limit = 15) => {
    if (!val || val === '—') return '—';
    const str = String(val);
    if (str.length <= limit) return str;
    return (
      <span
        onMouseMove={(e) => setMapTooltip({ x: e.clientX, y: e.clientY, content: str })}
        onMouseLeave={() => setMapTooltip(null)}
        style={{ cursor: 'pointer' }}
      >
        {str.substring(0, limit)}...
      </span>
    );
  };

  const [snapshots, setSnapshots] = useState([]);
  const [options, setOptions] = useState({
    states: [],
    filingTypes: [],
    companies: [],
    statuses: [],
    tois: [],
  });
  const [selectedId, setSelectedId] = useState(null);
  const lastIdRef = useRef(null);
  const lastSnapshotRef = useRef(null);
  if (selectedId) {
    lastIdRef.current = selectedId;
    const currentSnapshot = snapshots.find(f => f.serff_tracking_number === selectedId);
    if (currentSnapshot) lastSnapshotRef.current = currentSnapshot;
  }
  const activeId = selectedId || lastIdRef.current;
  const [drawerEvents, setDrawerEvents] = useState([]);
  const [drawerEventsLoading, setDrawerEventsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode] = useState(localStorage.getItem('comply_view_mode') || 'list'); // 'list' or 'grid'
  const [activePageTab, setActivePageTab] = useState(() => {
    return sessionStorage.getItem('comply_active_filings_tab') || 'Insights';
  });
  const [mapTooltip, setMapTooltip] = useState(null);
  const [mapView, setMapView] = useState('total'); // 'total', 'open', 'changes', 'objections'
  const isOurFilings = location.pathname === '/our-filings';
  const isAllFilings = location.pathname === '/rival-filings';
  const isCompetitors = location.pathname === '/competitors';
  const [scopeConfig, setScopeConfig] = useState({
    subjectCompany: '',
    competitors: []
  });
  const [scopeLoaded, setScopeLoaded] = useState(false);
  const TOP_COMPANY = scopeConfig.subjectCompany || '';
  const mappedCompetitors = useMemo(() => {
    const norm = (n) => String(n || '').trim().toLowerCase();
    const subjectKey = norm(TOP_COMPANY);
    return (scopeConfig.competitors || [])
      .map((c) => (typeof c === 'string' ? c : c?.name))
      .filter(Boolean)
      .filter((name) => !subjectKey || norm(name) !== subjectKey);
  }, [scopeConfig.competitors, TOP_COMPANY]);
  const hasMappedCompetitors = mappedCompetitors.length > 0;

  const subjectCompanyLabel = TOP_COMPANY || 'Our Client';
  const [tableFilterState, setTableFilterState] = useState('All states');
  const [tableFilterStatus, setTableFilterStatus] = useState('All statuses');
  const [tableFilterType, setTableFilterType] = useState('All filing types');
  const [tableFilterToi, setTableFilterToi] = useState('All TOI');
  const [tableFilterCompany, setTableFilterCompany] = useState('All companies');

  const [insightsFilterState, setInsightsFilterState] = useState('All states');
  const [insightsFilterStatus, setInsightsFilterStatus] = useState('All statuses');
  const [insightsFilterType, setInsightsFilterType] = useState('All filing types');
  const [insightsFilterToi, setInsightsFilterToi] = useState('All TOI');
  const [insightsFilterCompany, setInsightsFilterCompany] = useState('All companies');

  useEffect(() => {
    if (!isCompetitors || !mappedCompetitors.length) return;
    const normalizeCompanyFilter = (value, setter) => {
      if (!value || value === 'All companies') return;
      const parsed = parseMultiSelect(value, 'All companies', mappedCompetitors);
      if (!parsed.length) return;
      const next = serializeMultiSelect(parsed);
      if (next !== value) setter(next);
    };
    normalizeCompanyFilter(tableFilterCompany, setTableFilterCompany);
    normalizeCompanyFilter(insightsFilterCompany, setInsightsFilterCompany);
  }, [isCompetitors, mappedCompetitors, tableFilterCompany, insightsFilterCompany]);

  // Top Competitor: one filter set drives both Insights charts and Table (same drawer).
  const useSharedFilters = isCompetitors;
  const pickTabFilter = (insightsVal, tableVal) => (
    useSharedFilters ? tableVal : (activePageTab === 'Insights' ? insightsVal : tableVal)
  );
  const setBothOrTab = (tableSetter, insightsSetter) => (value) => {
    if (useSharedFilters) {
      tableSetter(value);
      insightsSetter(value);
    } else if (activePageTab === 'Insights') {
      insightsSetter(value);
    } else {
      tableSetter(value);
    }
  };

  const filterState = pickTabFilter(insightsFilterState, tableFilterState);
  const setFilterState = setBothOrTab(setTableFilterState, setInsightsFilterState);
  const filterStatus = pickTabFilter(insightsFilterStatus, tableFilterStatus);
  const setFilterStatus = setBothOrTab(setTableFilterStatus, setInsightsFilterStatus);
  const filterType = pickTabFilter(insightsFilterType, tableFilterType);
  const setFilterType = setBothOrTab(setTableFilterType, setInsightsFilterType);
  const filterToi = pickTabFilter(insightsFilterToi, tableFilterToi);
  const setFilterToi = setBothOrTab(setTableFilterToi, setInsightsFilterToi);
  const filterCompany = pickTabFilter(insightsFilterCompany, tableFilterCompany);
  const setFilterCompany = setBothOrTab(setTableFilterCompany, setInsightsFilterCompany);
  const tableActiveCompetitors = useMemo(
    () => resolveActiveCompetitorNames(filterCompany, mappedCompetitors),
    [filterCompany, mappedCompetitors],
  );
  const insightsActiveCompetitors = useMemo(
    () => resolveActiveCompetitorNames(filterCompany, mappedCompetitors),
    [filterCompany, mappedCompetitors],
  );
  const snapshotCompanyFilter = useMemo(() => {
    if (isOurFilings) return TOP_COMPANY ? [TOP_COMPANY] : undefined;
    if (isCompetitors) {
      if (!hasMappedCompetitors) return undefined;
      return tableActiveCompetitors.length ? tableActiveCompetitors : undefined;
    }
    return toApiFilterArray(filterCompany, 'All companies', options.companies);
  }, [
    isOurFilings,
    isCompetitors,
    TOP_COMPANY,
    filterCompany,
    tableActiveCompetitors,
    hasMappedCompetitors,
    options.companies,
  ]);
  const displayedSnapshots = isCompetitors
    ? (hasMappedCompetitors ? snapshots : [])
    : snapshots;
  const [subStart, setSubStart] = useState(null);
  const [subEnd, setSubEnd] = useState(null);
  const [tempSubRange, setTempSubRange] = useState([null, null]);
  const [dispStart, setDispStart] = useState(null);
  const [dispEnd, setDispEnd] = useState(null);
  const [tempDispRange, setTempDispRange] = useState([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [insightsData, setInsightsData] = useState(null);
  const topStatesDataResolved = useMemo(() => insightsData?.topStatesData || [], [insightsData]);
  const allStatesDataResolved = useMemo(() => insightsData?.allStatesData || [], [insightsData]);
  const geoStatesDataResolved = useMemo(
    () => (allStatesDataResolved.length ? allStatesDataResolved : topStatesDataResolved),
    [allStatesDataResolved, topStatesDataResolved],
  );
  const [openFilter, setOpenFilter] = useState(null);
  const [showTableFilters, setShowTableFilters] = useState(false);
  const filterClickCount = React.useRef(0);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.select-wrap')) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    getSnapshotOptions().then(res => setOptions(res.data.data));
  }, []);
  useEffect(() => {
    const fetchScope = async () => {
      try {
        const res = await getFilingsScopeConfig();
        if (res?.data?.data) {
          setScopeConfig({
            subjectCompany: res.data.data.subjectCompany || '',
            competitors: res.data.data.competitors || []
          });
        }
      } catch (e) {
        console.error('Error fetching filings scope config:', e);
      } finally {
        setScopeLoaded(true);
      }
    };
    fetchScope();
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
      setSelectedId(searchParam);
    }
    const tabParam = params.get('tab');
    if (tabParam === 'Alerts' && isCompetitors) {
      navigate('/competitors/alerts', { replace: true });
      return;
    }
    if (tabParam && (tabParam === 'Insights' || tabParam === 'Table')) {
      setActivePageTab(tabParam);
      sessionStorage.setItem('comply_active_filings_tab', tabParam);
    }
  }, [location.search, isCompetitors, navigate]);

  // Keep sidebar submenu highlight in sync: Insights/Table links require ?tab= on /competitors.
  useEffect(() => {
    if (!isCompetitors) return;
    const params = new URLSearchParams(location.search);
    if (params.get('tab')) return;
    const tab = sessionStorage.getItem('comply_active_filings_tab') || 'Insights';
    if (tab === 'Alerts') {
      navigate('/competitors/alerts', { replace: true });
      return;
    }
    const nextTab = tab === 'Table' ? 'Table' : 'Insights';
    const nextSearch = new URLSearchParams(location.search);
    nextSearch.set('tab', nextTab);
    navigate({ pathname: location.pathname, search: `?${nextSearch.toString()}` }, { replace: true });
  }, [isCompetitors, location.pathname, location.search, navigate]);

  // Reset filters when switching filings routes — not when ?tab= changes.
  useEffect(() => {
    if (isOurFilings) return;
    setTableFilterCompany('All companies');
    setInsightsFilterCompany('All companies');
  }, [location.pathname, isOurFilings]);

  useEffect(() => {
    if (isOurFilings && TOP_COMPANY) {
      setTableFilterCompany(TOP_COMPANY);
      setInsightsFilterCompany(TOP_COMPANY);
    }
  }, [isOurFilings, TOP_COMPANY]);

  useEffect(() => {
    if (!activeId) {
      setDrawerEvents([]);
      return undefined;
    }
    let cancelled = false;
    setDrawerEventsLoading(true);
    getEvents({ tracking_number: activeId, limit: 50 })
      .then((res) => {
        if (!cancelled) {
          setDrawerEvents(Array.isArray(res?.data?.data) ? res.data.data : []);
        }
      })
      .catch((err) => {
        console.error('Error fetching filing change events:', err);
        if (!cancelled) setDrawerEvents([]);
      })
      .finally(() => {
        if (!cancelled) setDrawerEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      if (!scopeLoaded) return;
      if (isCompetitors && !hasMappedCompetitors) {
        setSnapshots([]);
        setTotalRows(0);
        setLoading(false);
        return;
      }
      if (
        isCompetitors
        && filterCompany !== 'All companies'
        && tableActiveCompetitors.length === 0
      ) {
        setSnapshots([]);
        setTotalRows(0);
        setLoading(false);
        return;
      }
      try {
        const snapshotBody = {
          page: currentPage,
          limit: rowsPerPage,
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(subStart ? { subStart: format(subStart, 'yyyy-MM-dd') } : {}),
          ...(subEnd ? { subEnd: format(subEnd, 'yyyy-MM-dd') } : {}),
          ...(dispStart ? { dispStart: format(dispStart, 'yyyy-MM-dd') } : {}),
          ...(dispEnd ? { dispEnd: format(dispEnd, 'yyyy-MM-dd') } : {}),
          ...(snapshotCompanyFilter?.length ? { company: snapshotCompanyFilter } : {}),
        };
        assignFilterArray(snapshotBody, 'state', filterState, 'All states', options.states);
        assignFilterArray(
          snapshotBody,
          'status',
          filterStatus,
          'All statuses',
          options.statuses?.filter((s) => s !== 'All statuses'),
        );
        assignFilterArray(snapshotBody, 'filingType', filterType, 'All filing types', options.filingTypes);
        assignFilterArray(snapshotBody, 'toi', filterToi, 'All TOI', options.tois);
        const response = await getSnapshots(snapshotBody);
        setSnapshots(response.data?.data || []);
        setTotalRows(response.data?.meta?.totalItems || 0);
      } catch (error) {
        console.error('Error fetching snapshots:', error);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(() => {
      fetchSnapshots();
    }, 300);
    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchTerm, filterState, filterStatus, filterType, filterToi, filterCompany, subStart, subEnd, dispStart, dispEnd, isCompetitors, isOurFilings, hasMappedCompetitors, scopeLoaded, snapshotCompanyFilter, tableActiveCompetitors]);

  useEffect(() => {
    const fetchInsights = async () => {
      if (activePageTab !== 'Insights') return;
      if (!scopeLoaded) return;
      if (isCompetitors && !hasMappedCompetitors) {
        setInsightsData(null);
        return;
      }
      try {
        const mode = isOurFilings
          ? 'our-filings'
          : isCompetitors
            ? 'competitors'
            : isAllFilings
              ? 'all-filings'
              : 'all-filings';
        const insightsFilters = {};
        assignFilterArray(insightsFilters, 'state', filterState, 'All states', options.states);
        assignFilterArray(
          insightsFilters,
          'status',
          filterStatus,
          'All statuses',
          options.statuses?.filter((s) => s !== 'All statuses'),
        );
        assignFilterArray(insightsFilters, 'filingType', filterType, 'All filing types', options.filingTypes);
        assignFilterArray(insightsFilters, 'toi', filterToi, 'All TOI', options.tois);
        if (subStart) insightsFilters.subStart = format(subStart, 'yyyy-MM-dd');
        if (subEnd) insightsFilters.subEnd = format(subEnd, 'yyyy-MM-dd');
        if (dispStart) insightsFilters.dispStart = format(dispStart, 'yyyy-MM-dd');
        if (dispEnd) insightsFilters.dispEnd = format(dispEnd, 'yyyy-MM-dd');
        const payload = {
          mode,
          subjectCompany: TOP_COMPANY,
          competitors: isCompetitors ? insightsActiveCompetitors : [],
          search: searchTerm || '',
          filters: insightsFilters,
        };
        const res = await getFilingsInsights(payload);
        setInsightsData(res?.data?.data || null);
      } catch (e) {
        console.error('Error fetching filings insights:', e);
        setInsightsData(null);
      }
    };
    const t = setTimeout(fetchInsights, 350);
    return () => clearTimeout(t);
  }, [
    activePageTab,
    isOurFilings,
    isCompetitors,
    isAllFilings,
    searchTerm,
    filterState,
    filterStatus,
    filterType,
    filterToi,
    filterCompany,
    subStart,
    subEnd,
    dispStart,
    dispEnd,
    TOP_COMPANY,
    insightsActiveCompetitors,
    hasMappedCompetitors,
    scopeLoaded,
  ]);
  useEffect(() => {
    localStorage.setItem('comply_view_mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    sessionStorage.setItem('comply_active_filings_tab', activePageTab);
  }, [activePageTab]);

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const val = parseInt(pageInput);
    if (!isNaN(val) && val >= 1 && val <= totalPages) {
      setCurrentPage(val);
    } else {
      setPageInput(currentPage.toString());
    }
  };
  // Pagination logic
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const statuses = ['All statuses', ...(options.statuses || [])];
  const drawerTois = useMemo(
    () => (options.tois || []).filter((t) => t && String(t).trim() !== 'All TOI'),
    [options.tois],
  );
  const getStatusStyle = status => {
    if (!status) return { background: 'var(--c-white)', color: 'var(--text2)' };
    const s = status.toLowerCase();

    // 1. Completed States -> Timeline Emerald
    if (s.includes('approved') || s.includes('accepted')) {
      return { background: 'var(--bg-teal-soft)', color: 'var(--c-teal-active)' };
    }
    // 2. Attention/Negative States -> Timeline Orange
    if (s.includes('objection') || s.includes('info') || s.includes('question') || s.includes('reject') || s.includes('disapproved') || s.includes('withdrawn') || s.includes('closed')) {
      return { background: 'var(--bg-orange-soft)', color: 'var(--c-orange)' };
    }
    // 3. Active/In-Progress States -> Timeline Purple
    if (s.includes('review') || s.includes('pending')) {
      return { background: 'var(--bg-purple-soft)', color: 'var(--c-purple-1)' };
    }
    // 4. Initial States -> Timeline Blue
    if (s.includes('assigned') || s.includes('submitted') || s.includes('created')) {
      return { background: 'var(--bg-blue-soft)', color: 'var(--c-blue-1)' };
    }

    return { background: 'var(--bg-blue-soft)', color: 'var(--c-blue-1)' };
  };

  const findGeoStateEntry = useMemo(() => (stateCode) => (
    geoStatesDataResolved.find(
      (s) => s.state === stateCode || STATE_ABBREVIATIONS[s.state] === stateCode
    )
  ), [geoStatesDataResolved]);

  const getGeoStateVariant = useMemo(() => (stateCode, count = 0) => {
    const stateEntry = findGeoStateEntry(stateCode);
    const total = stateEntry?.total ?? count ?? 0;
    if (stateEntry?.subject > 0) return 'ours';
    if (total > 0) return 'other';
    return 'empty';
  }, [findGeoStateEntry]);

  const getGeoStateTooltip = useMemo(() => (stateName, stateCode, mapCount = 0) => {
    const stateEntry = findGeoStateEntry(stateCode);

    if (mapView === 'total') {
      const subject = stateEntry?.subject ?? 0;
      const total = stateEntry?.total ?? mapCount ?? 0;
      if (total > 0) {
        return `${stateName}: ${subject} Our Filings / ${total} Total`;
      }
      return `${stateName}: No filings recorded`;
    }

    const value = stateEntry?.count ?? mapCount ?? 0;
    if (value > 0) {
      const label = mapView.charAt(0).toUpperCase() + mapView.slice(1);
      return `${stateName}: ${value} ${label}`;
    }
    return `${stateName}: No filings recorded`;
  }, [findGeoStateEntry, mapView]);

  const currentMapData = insightsData?.mapDataByView?.[mapView] || insightsData?.mapData || {};
  const resolveMapValueCount = (value) => {
    if (value && typeof value === 'object') {
      const v = Number(value.total ?? value.count ?? value.value ?? 0);
      return Number.isFinite(v) ? v : 0;
    }
    const n = Number(value || 0);
    return Number.isFinite(n) ? n : 0;
  };
  const mapEntries = Object.entries(currentMapData)
    .map(([state, count]) => ({ state, count: resolveMapValueCount(count) }))
    .filter(entry => entry.count > 0)
    .sort((a, b) => b.count - a.count);

  const resolvedMapEntries = useMemo(() => {
    if (mapView === 'total') {
      return geoStatesDataResolved.map(s => ({
        state: STATE_ABBREVIATIONS[s.state] || s.state,
        count: s.total,
        subject: s.subject,
        alert: s.alert
      }));
    }
    return mapEntries;
  }, [mapView, mapEntries, geoStatesDataResolved]);

  const resolvedRankingEntries = useMemo(() => {
    if (mapView === 'total') {
      return topStatesDataResolved.map(s => ({
        state: STATE_ABBREVIATIONS[s.state] || s.state,
        count: s.total,
        subject: s.subject,
        alert: s.alert,
      }));
    }
    return mapEntries;
  }, [mapView, mapEntries, topStatesDataResolved]);

  const geoRankingRowGap = useMemo(() => {
    const visibleCount = resolvedRankingEntries.length;
    if (visibleCount <= 1) return GEO_RANKING_GAP_MIN;

    const rowsHeight = visibleCount * GEO_RANKING_BAR_HEIGHT;
    const minGapsTotal = (visibleCount - 1) * GEO_RANKING_GAP_MIN;
    const contentMin = GEO_RANKING_HEADER_PX + rowsHeight + minGapsTotal + GEO_RANKING_CHART_PADDING_BOTTOM;
    const surplus = Math.max(0, GEO_MAP_TARGET_HEIGHT - contentMin);
    const extraPerGap = Math.floor(surplus / (visibleCount - 1));

    return Math.min(GEO_RANKING_GAP_MAX, GEO_RANKING_GAP_MIN + extraPerGap);
  }, [resolvedRankingEntries.length]);

  const formatDate = dateStr => {
    if (!dateStr || dateStr === '—') return '—';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };
  const filingVolumeDataResolved = insightsData?.filingVolumeData || [];
  const outcomeDataResolved = insightsData?.outcomeData || [];
  const competitorVolumeDataResolved = insightsData?.competitorVolumeData || null;
  const competitorCadenceDataResolved = insightsData?.competitorCadenceData || [];
  const competitorCadenceWeeksResolved = insightsData?.weeks || [];
  const emptyProductDepth = { categories: [], dataByCompany: null, maxVal: 0 };
  const productLifeDataResolved = insightsData?.productLifeData || emptyProductDepth;
  const productAnnuityDataResolved = insightsData?.productAnnuityData || emptyProductDepth;
  const productMedicareDataResolved = insightsData?.productMedicareData || emptyProductDepth;
  const productHealthDataResolved = insightsData?.productHealthData || emptyProductDepth;
  const productDepthColumns = [
    { title: 'Life Insurance', data: productLifeDataResolved },
    { title: 'Annuities', data: productAnnuityDataResolved },
    { title: 'Medicare / Supplement', data: productMedicareDataResolved },
    { title: 'Health & Other', data: productHealthDataResolved },
  ];
  const kpiCardsResolved = insightsData?.kpiCards || [];
  const hasRenderableProductData = (payload) => {
    if (!payload?.categories?.length || !payload?.dataByCompany) return false;
    return payload.categories.some((cat) =>
      Object.values(payload.dataByCompany).some((row) => Number(row?.[cat] || 0) > 0)
    );
  };

  const hasFiltersApplied = filterState !== 'All states' ||
    filterStatus !== 'All statuses' ||
    filterType !== 'All filing types' ||
    filterToi !== 'All TOI' ||
    (filterCompany !== 'All companies' && !isOurFilings) ||
    subStart !== null ||
    subEnd !== null ||
    dispStart !== null ||
    dispEnd !== null;

  if (loading) return <div className="content">Loading filings...</div>;
  const showNoCompetitorsConfigured = isCompetitors && scopeLoaded && !hasMappedCompetitors;
  return <>
    <PageHeader
      title={isOurFilings ? 'Our Filings' : isAllFilings ? 'All Filings' : isCompetitors ? 'State Insurance Filings' : 'Filings'}
      activePageTab={activePageTab}
      searchTerm={searchTerm}
      onSearchChange={e => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
      }}
      hasFiltersApplied={hasFiltersApplied}
      onClearFilters={() => {
        setFilterState('All states');
        setFilterStatus('All statuses');
        setFilterType('All filing types');
        setFilterToi('All TOI');
        if (!isOurFilings) setFilterCompany('All companies');
        setSubStart(null);
        setSubEnd(null);
        setTempSubRange([null, null]);
        setDispStart(null);
        setDispEnd(null);
        setTempDispRange([null, null]);
        setCurrentPage(1);
      }}
      showTableFilters={showTableFilters}
      onToggleFilters={() => setShowTableFilters(!showTableFilters)}
    />

    {showNoCompetitorsConfigured && (
      <p className="assistants-status" style={{ margin: '0 24px 12px' }}>
        No competitors are configured for your account. Contact your administrator to add Intel Axis competitors.
      </p>
    )}

    <div className="body">
      {activePageTab === 'Table' ? <> <div key={`table-${location.key}`} className="table-area filings-table-panel">

        {/* List View */}
        {viewMode === 'list' && <div key={`list-${currentPage}-${rowsPerPage}-${totalRows}`} className="table-wrap">
          <table>
            <colgroup>
              <col className="col-tn" />
              <col className="col-company" />
              <col className="col-state" />
              <col className="col-type" />
              <col className="col-toi" />
              <col className="col-sub-toi" />
              <col className="col-product-name" />
              <col className="col-status" />
              <col className="col-date" />
              <col className="col-date" />
            </colgroup>
            <thead>
              <tr>
                <th className="col-tn"><div className="th-sort">Tracking #</div></th>
                <th className="col-company"><div className="th-sort">Company</div></th>
                <th className="col-state">State</th>
                <th className="col-type">Filing Type</th>
                <th className="col-toi">TOI</th>
                <th className="col-sub-toi">Sub-TOI</th>
                <th className="col-product-name">Product Name</th>
                <th className="col-status">SERFF Status</th>
                <th className="col-date"><div className="th-sort">Submitted</div></th>
                <th className="col-date">Disposition</th>
              </tr>
            </thead>
            <tbody>
              {displayedSnapshots.map((s, rowIdx) => (
                <tr
                  key={s.serff_tracking_number}
                  className={selectedId === s.serff_tracking_number ? 'selected' : ''}
                  style={{ '--table-row-delay': rowIdx }}
                  onClick={() => setSelectedId(s.serff_tracking_number)}
                >
                  <td><span className="tn">{formatValue(s.serff_tracking_number)}</span></td>
                  <td className="company-cell">
                    <CompanyBrandCell
                      companyName={s.company_name}
                      subjectCompany={TOP_COMPANY}
                    />
                  </td>
                  <td>{s.state}</td>
                  <td>{formatValue(s.filing_type)}</td>
                  <td>{formatValue(s.toi)}</td>
                  <td>{formatValue(s.sub_toi)}</td>
                  <td>{formatValue(s.product_name)}</td>
                  <td><span className="pill" style={getStatusStyle(s.serff_status)}>{s.serff_status || '—'}</span></td>
                  <td className="date-cell">{formatDate(s.submission_date)}</td>
                  <td className="date-cell">{formatDate(s.disposition_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}

        {/* Grid View */}
        {viewMode === 'grid' && <div key={`grid-${currentPage}-${rowsPerPage}-${totalRows}`} className="filings-grid">
          {displayedSnapshots.map((s, rowIdx) => <div key={s.serff_tracking_number} className={`filing-card ${selectedId === s.serff_tracking_number ? 'selected' : ''}`} style={{ '--table-row-delay': rowIdx }} onClick={() => setSelectedId(s.serff_tracking_number)}>
            <div className="card-header">
              <span className="card-tn">{s.serff_tracking_number}</span>
              <span className="pill pill-outline">{s.state}</span>
            </div>
            <div className="card-company">{s.company_name}</div>
            <div className="card-body">
              <div className="card-pills">
                <span className="pill" style={getStatusStyle(s.filing_status)}>{s.filing_status}</span>
                <span className="pill pill-outline">{s.filing_type}</span>
              </div>
            </div>
            <div className="card-footer">
              <span>Submitted: {formatDate(s.submission_date)}</span>
              {s.disposition_date && <span className="pill" style={getStatusStyle('closed')}>Closed</span>}
            </div>
          </div>)}
        </div>}

        {/* Pagination */}
        <div className="pagination pagination-sticky">
          <div className="pg-info">
            Showing {totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows} filings
          </div>
          <div className="table-pagination">
            <div className="table-pagination__control">
              <span className="table-pagination__label">Rows per page</span>
              <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
                <span className="select-measure table-pagination__measure">{rowsPerPage}</span>
                <select className="filter-select table-pagination__select" value={rowsPerPage} onFocus={() => {
                  filterClickCount.current = 0;
                  setOpenFilter('rows');
                }} onBlur={() => {
                  filterClickCount.current = 0;
                  setOpenFilter(null);
                }} onClick={e => {
                  filterClickCount.current++;
                  if (filterClickCount.current > 1) {
                    e.target.blur();
                  }
                }} onChange={e => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                  e.target.blur();
                }}>
                  <option value={25} className="table-pagination__option">25</option>
                  <option value={35} className="table-pagination__option">35</option>
                  <option value={50} className="table-pagination__option">50</option>
                </select>
              </div>
            </div>
            <div className="pg-controls">
              <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                <ChevronLeft size={14} />
              </button>

              <form onSubmit={handlePageInputSubmit} className="companydetails-style-33">
                <input
                  type="text"
                  className="pg-input"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={() => setPageInput(currentPage.toString())}
                />
                <button type="submit" style={{ display: 'none' }} />
              </form>

              <button className="pg-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

        <div className={`drawer ${selectedId ? 'drawer-open' : ''}`}>
          {(() => {
            const s = snapshots.find(f => f.serff_tracking_number === activeId) || lastSnapshotRef.current || { serff_tracking_number: '', submission_date: new Date() };
            const docTotal = (s.form_docs || 0) + (s.rate_rule_docs || 0) + (s.supporting_docs || 0) + (s.correspondence_count || 0);
            if (!s && !activeId) return null;
            return (
              <>
                <div className="drawer-header">
                  <div>
                    <div className="drawer-tn">{s.serff_tracking_number}</div>
                    <div className="drawer-company">{s.company_name}</div>
                    <div className="drawer-toi">{s.toi || '—'} — {s.filing_type} · {s.state}</div>
                  </div>
                  <button className="drawer-close filters-drawer-close" onClick={() => setSelectedId(null)}>×</button>
                </div>

                <div className="drawer-tabs">
                  <div className="dt active">Details</div>
                </div>

                <div className="drawer-body">
                  <div className="detail-section">
                    <div className="ds-title">Current status</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="pill" style={getStatusStyle(s.serff_status)}>{s.serff_status || '—'}</span>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title">Key dates</div>
                    <div className="field-grid">
                      <div className="field">
                        <div className="field-label">Submitted</div>
                        <div className="field-val">{formatDate(s.submission_date)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Respond by</div>
                        <div className="field-val">{formatDate(new Date(new Date(s.submission_date).getTime() + 6 * 24 * 60 * 60 * 1000))}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">State status changed</div>
                        <div className="field-val">{formatDate(s.state_status_last_changed)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Disposition date</div>
                        <div className="field-val" style={{ color: s.disposition_date ? 'var(--c-black)' : 'var(--text3)' }}>{formatDate(s.disposition_date) || 'Not set'}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">First seen</div>
                        <div className="field-val">{formatDate(s.submission_date)}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Last updated</div>
                        <div className="field-val">{formatDate(s.fetched_at)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title">Filing info</div>
                    <div className="field-grid">
                      <div className="field">
                        <div className="field-label">State</div>
                        <div className="field-val">{s.state}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">NAIC code</div>
                        <div className="field-val">{s.naic_code || '—'}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Filing type</div>
                        <div className="field-val">{s.filing_type}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">TOI</div>
                        <div className="field-val">{s.toi || '—'}</div>
                      </div>
                      <div className="field">
                        <div className="field-label">Sub TOI</div>
                        <div className="field-val">{s.sub_toi || '—'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title">Documents — {docTotal} total</div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', paddingTop: '8px' }}>
                      <div className="pb-col">
                        <div className="pb-bar" style={{ height: `${Math.min(40, (s.form_docs || 0) * 3 + 4)}px`, background: 'var(--bg-blue-soft)', border: '1px solid var(--c-blue-1)' }}></div>
                        <div className="pb-label" style={{ color: 'var(--c-blue-1)' }}>Form</div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.form_docs || 0}</div>
                      </div>
                      <div className="pb-col">
                        <div className="pb-bar" style={{ height: `${Math.min(40, (s.rate_rule_docs || 0) * 3 + 4)}px`, background: 'var(--bg-purple-soft)', border: '1px solid var(--c-purple-1)' }}></div>
                        <div className="pb-label" style={{ color: 'var(--c-purple-1)' }}>Rate</div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.rate_rule_docs || 0}</div>
                      </div>
                      <div className="pb-col">
                        <div className="pb-bar" style={{ height: `${Math.min(40, (s.supporting_docs || 0) * 3 + 4)}px`, background: 'var(--bg-teal-soft)', border: '1px solid var(--c-teal-active)' }}></div>
                        <div className="pb-label" style={{ color: 'var(--c-teal-active)' }}>Supp</div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.supporting_docs || 0}</div>
                      </div>
                      <div className="pb-col">
                        <div className="pb-bar" style={{ height: `${Math.min(40, (s.correspondence_count || 0) * 3 + 4)}px`, background: 'var(--bg-orange-soft)', border: '1px solid var(--c-orange)' }}></div>
                        <div className="pb-label" style={{ color: 'var(--c-orange)' }}>Corr</div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-black)', textAlign: 'center' }}>{s.correspondence_count || 0}</div>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <div className="ds-title">Recent changes</div>
                    <div style={{ paddingTop: '8px' }}>
                      {drawerEventsLoading ? (
                        <div className="page-empty page-empty--compact">Loading changes…</div>
                      ) : drawerEvents.length === 0 ? (
                        <div className="page-empty page-empty--compact">No change history for this filing.</div>
                      ) : (
                        drawerEvents.map((event, eventIdx) => {
                          const isLast = eventIdx === drawerEvents.length - 1;
                          const dotClass = changeEventDotClass(event.change_type);
                          return (
                            <div key={event.id || `${event.change_type}-${event.detected_at}-${eventIdx}`} className="change-entry">
                              <div className="ce-dot-wrap">
                                <div className={`ce-dot ${dotClass}`} />
                                {!isLast ? <div className="ce-line" /> : null}
                              </div>
                              <div className="ce-body">
                                <div className="ce-time">{formatDate(event.detected_at)}</div>
                                <div className="ce-type">{formatChangeEventType(event.change_type)}</div>
                                <div className="ce-detail">{formatChangeEventDetail(event)}</div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                </div>
                <div className="drawer-actions">
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      alert("Send Email clicked!");
                    }}
                  >
                    Send Email
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => {
                      alert("Download PDF clicked!");
                    }}
                  >
                    Download
                  </button>
                  <button
                    className="btn btn-sm btn-filters"
                    onClick={() => {
                      sessionStorage.setItem('comply_last_filings_path', location.pathname);
                      sessionStorage.setItem('comply_active_filings_tab', activePageTab);
                      navigate(`/filings/${encodeURIComponent(s.serff_tracking_number || '')}`);
                    }}
                  >
                    More Info
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </>
        : activePageTab === 'Insights' ? <div key={`insights-${location.key}`} className="table-area insights-panel">

          {isOurFilings && <div className="report-outer report-outer--embedded report-outer--flush">
            <div className="report-scope">
              <div className="report-page report-page--flush">
                <div className="header">
                  <div className="header-left">
                    <div className="header-divider" />
                    <div className="header-text">
                      <span className="report-label">Our Corporate Filing Intelligence</span>
                      <span className="report-title">{subjectCompanyLabel}</span>
                    </div>
                  </div>
                  <div className="header-right">
                    <span className="week-badge">Active Period</span>
                    <br />
                    Internal Confidential
                  </div>
                </div>

                {/* KPI Grid */}
                <div className="report-card insights-card">
                  <div className="insights-section-title">Filing Performance Stats</div>
                  <div className="kpi-strip">
                    <div className="kpi-strip__col">
                      <div className="kpi-strip__head">
                        <div>
                          <div className="kpi-strip__label">Our Total Filings</div>
                          <div className="kpi-strip__value">128</div>
                        </div>
                        <div className="kpi-strip__aside">
                          <div className="kpi-delta kpi-delta--up">
                            <ArrowUp size={10} /> 8.4%
                          </div>
                          <div className="kpi-delta__hint">From last month</div>
                        </div>
                      </div>
                    </div>
                    <div className="kpi-strip__divider"></div>
                    <div className="kpi-strip__col">
                      <div className="kpi-strip__head">
                        <div>
                          <div className="kpi-strip__label">Approval Rate</div>
                          <div className="kpi-strip__value">96.8%</div>
                        </div>
                        <div className="kpi-strip__aside">
                          <div className="kpi-delta kpi-delta--up">
                            <ArrowUp size={10} /> 1.2%
                          </div>
                          <div className="kpi-delta__hint">Industry leading</div>
                        </div>
                      </div>
                    </div>
                    <div className="kpi-strip__divider"></div>
                    <div className="kpi-strip__col">
                      <div className="kpi-strip__head">
                        <div>
                          <div className="kpi-strip__label">Avg Days to Approved</div>
                          <div className="kpi-strip__value">14.2</div>
                        </div>
                        <div className="kpi-strip__aside">
                          <div className="kpi-delta kpi-delta--down">
                            <ArrowDown size={10} /> 0.5d
                          </div>
                          <div className="kpi-delta__hint">Speed-to-market</div>
                        </div>
                      </div>
                    </div>
                    <div className="kpi-strip__divider"></div>
                    <div className="kpi-strip__col">
                      <div className="kpi-strip__head">
                        <div>
                          <div className="kpi-strip__label">Active Backlog</div>
                          <div className="kpi-strip__value">0</div>
                        </div>
                        <div className="kpi-strip__aside">
                          <div className="kpi-delta kpi-delta--neutral">
                            Stable
                          </div>
                          <div className="kpi-delta__hint">Clean queue</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="insights-row">
                  {/* Strategic Impact Metrics */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Strategic Impact Metrics</div>
                    <div className="metric-panel">
                      <div className="metric-panel__spacer"></div>
                      <div className="metric-panel__list">
                        {[{
                          label: 'Market Value Gen.',
                          val: 85,
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: 'Forecasted Savings',
                          val: 65,
                          color: CANONICAL_SPECTRUM[8]
                        }, {
                          label: 'Total Capital Won',
                          val: 45,
                          color: CANONICAL_SPECTRUM[16]
                        }, {
                          label: 'Avg. Filing Value',
                          val: 30,
                          color: CANONICAL_SPECTRUM[24]
                        }].map((item, idx) => <div key={idx} className="metric-row">
                          <div className="metric-row__label">{item.label}</div>
                          <div className="metric-row__track">
                            <div style={{
                              width: `${item.val}%`,
                              ...getPbBarStyle(item.color)
                            }} className="metric-row__fill"></div>
                          </div>
                        </div>)}
                      </div>
                    </div>
                  </div>

                  {/* Filing Volume by Stage */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Filing Volume by Stage</div>
                    <div className="insights-chart-area insights-chart-area--md">
                      {filingVolumeDataResolved.length > 0 ? (
                        <FilingVolumeChart data={filingVolumeDataResolved} />
                      ) : (
                        <div className="insights-chart-area insights-chart-area--md">No data present</div>
                      )}
                    </div>
                  </div>

                  {/* Avg. Review Days by Stage */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Avg. Review Days by Stage</div>
                    <div className="metric-bars">
                      {[{
                        label: 'Intake',
                        val: 4,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        label: 'Quality Check',
                        val: 12,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        label: 'State Review',
                        val: 45,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        label: 'Negotiation',
                        val: 28,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        label: 'Final Approval',
                        val: 8,
                        color: CANONICAL_SPECTRUM[0]
                      }].map((item, idx) => <div key={idx} className="metric-bars__row">
                        <div className="metric-bars__label">{item.label}</div>
                        <div className="metric-bars__track">
                          <div style={{
                            width: `${item.val / 50 * 100}%`,
                            ...getPbBarStyle(item.color)
                          }} className="metric-bars__fill"></div>
                          <span style={{
                            color: 'var(--c-black)'
                          }} className="metric-bars__value">{item.val}d</span>
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div>

                <div className="insights-row">
                  {/* Submission Trends (Weekly) */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Submission Trends (Weekly)</div>
                    <div className="insights-chart-area insights-chart-area--lg">
                      <svg width="100%" height="160" viewBox="0 0 500 160">
                        {[0, 1, 2, 3, 4].map(i => <line key={i} x1="0" y1={i * 40} x2="500" y2={i * 40} stroke="var(--border)" strokeWidth="1" />)}
                        <path d="M 0 140 L 125 100 L 250 120 L 375 80 L 500 40" fill="transparent" stroke={CANONICAL_SPECTRUM[0]} strokeWidth="2" className="insights-chart-trend-line" style={{ '--line-length': 560 }} />
                        <path d="M 0 160 L 125 140 L 250 150 L 375 130 L 500 110" fill="transparent" stroke={CANONICAL_SPECTRUM[15]} strokeWidth="2" strokeDasharray="4 4" className="insights-chart-trend-line insights-chart-trend-line--delayed" style={{ '--line-length': 520 }} />
                        {[0, 125, 250, 375, 500].map((x, i) => (
                          <g key={i} className="insights-chart-line-point" style={{ '--point-delay': `${0.5 + i * 0.08}s` }}>
                            <circle cx={x} cy={[140, 100, 120, 80, 40][i]} r="4" fill="var(--c-white)" stroke={CANONICAL_SPECTRUM[0]} strokeWidth="2" />
                          </g>
                        ))}
                      </svg>
                      <div className="insights-chart-labels">
                        {['Apr 06', 'Apr 13', 'Apr 20', 'Apr 27', 'May 04'].map(d => <div key={d} className="insights-chart-labels__item">{d}</div>)}
                      </div>
                      <div className="insights-chart-legend">
                        <div className="insights-chart-legend__item">
                          <div className="insights-chart-legend__swatch" style={{ background: CANONICAL_SPECTRUM[0] }}></div> New Submissions
                        </div>
                        <div className="insights-chart-legend__item">
                          <div className="insights-chart-legend__swatch" style={{ background: CANONICAL_SPECTRUM[15] }}></div> Industry Avg.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filings by Market Category Donut */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Filings by Market Category</div>
                    <div className="insights-chart-area insights-chart-area--md">
                      <svg width="224" height="224" viewBox="0 0 224 224">
                        <circle cx="112" cy="112" r="96" fill="none" stroke="var(--border)" strokeWidth="32" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[0]} strokeWidth="32" strokeDasharray="192 603" strokeDashoffset="0" className="insights-chart-donut-segment" style={{ transitionDelay: '0.15s' }} />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[8]} strokeWidth="32" strokeDasharray="134 603" strokeDashoffset="-192" className="insights-chart-donut-segment" style={{ transitionDelay: '0.25s' }} />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[16]} strokeWidth="32" strokeDasharray="96 603" strokeDashoffset="-326" className="insights-chart-donut-segment" style={{ transitionDelay: '0.35s' }} />
                        <text x="112" y="117" textAnchor="middle" fill="var(--c-black)" fontSize="16" fontWeight="600">Categories</text>
                      </svg>
                      <div className="insights-donut-legend">
                        {[{
                          label: 'Personal',
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: 'Commercial',
                          color: CANONICAL_SPECTRUM[5]
                        }, {
                          label: 'Life',
                          color: CANONICAL_SPECTRUM[10]
                        }, {
                          label: 'Health',
                          color: CANONICAL_SPECTRUM[15]
                        }, {
                          label: 'Annuity',
                          color: CANONICAL_SPECTRUM[20]
                        }, {
                          label: 'Other',
                          color: CANONICAL_SPECTRUM[25]
                        }].map((item, idx) => <div key={idx} className="insights-donut-legend__item">
                          <div style={{
                            ...getPbBarStyle(item.color)
                          }} className="insights-donut-legend__swatch"></div> {item.label}
                        </div>)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="insights-row">
                  {/* Filing Pipeline Metrics */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Filing Pipeline</div>
                    <div className="metric-panel">
                      <div className="metric-panel__list">
                        {[{
                          label: '$ Pipeline Generated',
                          val: 90,
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: '$ Total Forecasted',
                          val: 70,
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: '$ Total Won',
                          val: 50,
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: '$ Avg. Amount',
                          val: 30,
                          color: CANONICAL_SPECTRUM[0]
                        }].map((item, idx) => <div key={idx} className="metric-row">
                          <div className="metric-row__label">{item.label}</div>
                          <div className="metric-row__track">
                            <div style={{
                              width: `${item.val}%`,
                              ...getPbBarStyle(item.color)
                            }} className="metric-row__fill"></div>
                          </div>
                        </div>)}
                      </div>
                    </div>
                  </div>

                  {/* Outcome Distribution */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Outcome Distribution</div>
                    <div className="insights-chart-area insights-chart-area--md">
                      {outcomeDataResolved.length > 0 ? (
                        <OutcomeDistributionChart data={outcomeDataResolved} />
                      ) : (
                        <div className="insights-chart-area insights-chart-area--md">No data present</div>
                      )}
                    </div>
                  </div>

                  {/* Jurisdictional Friction */}
                  <div className="report-card insights-card">
                    <div className="insights-section-title">Jurisdictional Friction</div>
                    <div className="metric-bars">
                      {[{
                        state: 'Florida',
                        val: 85,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        state: 'California',
                        val: 65,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        state: 'Texas',
                        val: 40,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        state: 'New York',
                        val: 95,
                        color: CANONICAL_SPECTRUM[0]
                      }, {
                        state: 'Illinois',
                        val: 30,
                        color: CANONICAL_SPECTRUM[0]
                      }].map((item, idx) => <div key={idx} className="metric-bars__row">
                        <div className="metric-bars__label">{item.state}</div>
                        <div className="metric-bars__track">
                          <div style={{
                            width: `${item.val}%`,
                            ...getPbBarStyle(item.color)
                          }} className="metric-bars__fill"></div>
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div>

                <div className="footer insights-footer-note">
                  <span>Powered by <strong>www.thecomply.ai</strong></span>
                  <span>CONFIDENTIAL — Internal Corporate Use Only · {subjectCompanyLabel}</span>
                </div>
              </div>
            </div>
          </div>}

          {isAllFilings && <div className="report-outer report-outer--embedded insights-panel">
            <div className="report-scope">
              <div className="report-page report-outer--flush">
                <div className="header">
                  <div className="header-left">
                    <div className="header-divider" />
                    <div className="header-text">
                      <span className="report-label">Competitor Market Analysis</span>
                      <span className="report-title">All Filings Intelligence Overview</span>
                    </div>
                  </div>
                  <div className="header-right">
                    <span className="week-badge">Active Period</span>
                    <br />
                    Market Intelligence
                  </div>
                </div>

                <div className="kpi-delta__hint">
                  {/* Strategic Impact Metrics */}
                  <div className="report-card insights-row">
                    <div className="insights-card">Market Strategic Impact</div>
                    <div className="insights-section-title">
                      <div className="metric-panel"></div>
                      <div className="metric-panel__spacer">
                        {[{
                          label: 'Market Expansion',
                          val: 75,
                          color: CANONICAL_SPECTRUM[10]
                        }, {
                          label: 'Rate Optimization',
                          val: 90,
                          color: CANONICAL_SPECTRUM[16]
                        }, {
                          label: 'Loss Ratio Impact',
                          val: 40,
                          color: CANONICAL_SPECTRUM[22]
                        }, {
                          label: 'Product Churn',
                          val: 25,
                          color: CANONICAL_SPECTRUM[28]
                        }].map((item, idx) => <div key={idx} className="metric-panel__list">
                          <div className="metric-row">{item.label}</div>
                          <div className="metric-row__label">
                            <div style={{
                              width: `${item.val}%`,
                              ...getPbBarStyle(item.color)
                            }} className="metric-row__track"></div>
                          </div>
                        </div>)}
                      </div>
                    </div>
                  </div>

                  {/* Filing Volume by Stage */}
                  <div className="report-card metric-row__fill">
                    <div className="insights-card">Market Volume by Stage</div>
                    <div className="insights-section-title">
                      <svg width="100%" height="140" viewBox="0 0 300 140">
                        <line x1="0" y1="130" x2="300" y2="130" stroke="var(--border)" strokeWidth="1" />
                        <path d="M 0 130 Q 50 110 100 80 T 200 100 T 300 40 L 300 130 L 0 130 Z" fill={CANONICAL_SPECTRUM[15]} opacity="0.05" />
                        <path d="M 0 130 Q 50 110 100 80 T 200 100 T 300 40" fill="transparent" stroke={CANONICAL_SPECTRUM[15]} strokeWidth="2" />
                        {[0, 100, 200, 300].map((x, i) => <circle key={i} cx={x} cy={[130, 80, 100, 40][i]} r="3" fill={CANONICAL_SPECTRUM[15]} />)}
                      </svg>
                      <div className="insights-chart-area insights-chart-area--md">
                        {['Lead', 'Review', 'Pending', 'Approved'].map(s => <div key={s} className="insights-chart-placeholder">{s}</div>)}
                      </div>
                    </div>
                  </div>

                  {/* Avg. Review Days by Stage */}
                  <div className="report-card insights-chart-placeholder__bar">
                    <div className="insights-card">Market Avg. Review Days</div>
                    <div className="insights-section-title">
                      {[{
                        label: 'Intake',
                        val: 6,
                        color: CANONICAL_SPECTRUM[15]
                      }, {
                        label: 'Quality Check',
                        val: 15,
                        color: CANONICAL_SPECTRUM[15]
                      }, {
                        label: 'State Review',
                        val: 52,
                        color: CANONICAL_SPECTRUM[15]
                      }, {
                        label: 'Negotiation',
                        val: 34,
                        color: CANONICAL_SPECTRUM[15]
                      }, {
                        label: 'Final Approval',
                        val: 10,
                        color: CANONICAL_SPECTRUM[15]
                      }].map((item, idx) => <div key={idx} className="metric-bars">
                        <div className="metric-bars__row">{item.label}</div>
                        <div className="metric-bars__label">
                          <div style={{
                            width: `${item.val / 60 * 100}%`,
                            ...getPbBarStyle(item.color)
                          }} className="metric-bars__track"></div>
                          <span style={{
                            color: item.val > 20 ? 'var(--c-white)' : 'var(--c-black)'
                          }} className="metric-bars__fill">{item.val}d</span>
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div>

                <div className="metric-bars__value">
                  {/* Submission Trends (Weekly) */}
                  <div className="report-card insights-row">
                    <div className="insights-card">Market Submission Trends</div>
                    <div className="insights-section-title">
                      <svg width="100%" height="160" viewBox="0 0 500 160">
                        {[0, 1, 2, 3, 4].map(i => <line key={i} x1="0" y1={i * 40} x2="500" y2={i * 40} stroke="var(--border)" strokeWidth="1" />)}
                        <path d="M 0 120 L 125 140 L 250 80 L 375 100 L 500 60" fill="transparent" stroke={CANONICAL_SPECTRUM[15]} strokeWidth="2" />
                        <path d="M 0 100 L 125 80 L 250 110 L 375 60 L 500 40" fill="transparent" stroke={CANONICAL_SPECTRUM[25]} strokeWidth="2" strokeDasharray="4 4" />
                        {[0, 125, 250, 375, 500].map((x, i) => <circle key={i} cx={x} cy={[120, 140, 80, 100, 60][i]} r="4" fill="var(--c-white)" stroke={CANONICAL_SPECTRUM[15]} strokeWidth="2" />)}
                      </svg>
                      <div className="insights-chart-area insights-chart-area--lg">
                        {['Apr 06', 'Apr 13', 'Apr 20', 'Apr 27', 'May 04'].map(d => <div key={d} className="insights-chart-labels">{d}</div>)}
                      </div>
                      <div className="insights-chart-labels__item">
                        <div className="insights-chart-legend">
                          <div className="insights-chart-legend__item" style={{ background: CANONICAL_SPECTRUM[15] }}></div> Total Market
                        </div>
                        <div className="insights-chart-legend__swatch">
                          <div className="insights-chart-legend__item" style={{ background: CANONICAL_SPECTRUM[25] }}></div> Competitor Set
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filings by Market Category Donut */}
                  <div className="report-card insights-chart-legend__swatch">
                    <div className="insights-card">Market Category Distribution</div>
                    <div className="insights-section-title">
                      <svg width="224" height="224" viewBox="0 0 224 224">
                        <circle cx="112" cy="112" r="96" fill="none" stroke="var(--border)" strokeWidth="32" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[15]} strokeWidth="32" strokeDasharray="230 603" strokeDashoffset="0" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[20]} strokeWidth="32" strokeDasharray="173 603" strokeDashoffset="-230" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[25]} strokeWidth="32" strokeDasharray="115 603" strokeDashoffset="-403" />
                        <text x="112" y="117" textAnchor="middle" fill="var(--c-black)" fontSize="16" fontWeight="600">Market</text>
                      </svg>
                      <div className="insights-chart-area insights-chart-area--md">
                        {[{
                          label: 'Auto',
                          color: CANONICAL_SPECTRUM[0]
                        }, {
                          label: 'Property',
                          color: CANONICAL_SPECTRUM[6]
                        }, {
                          label: 'Liability',
                          color: CANONICAL_SPECTRUM[12]
                        }, {
                          label: 'Workers Comp',
                          color: CANONICAL_SPECTRUM[18]
                        }, {
                          label: 'Health',
                          color: CANONICAL_SPECTRUM[24]
                        }, {
                          label: 'Other',
                          color: CANONICAL_SPECTRUM[29]
                        }].map((item, idx) => <div key={idx} className="insights-donut-legend">
                          <div style={{
                            ...getPbBarStyle(item.color)
                          }} className="insights-donut-legend__item"></div> {item.label}
                        </div>)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="insights-donut-legend__swatch">
                  {/* Market Pipeline Metrics */}
                  <div className="report-card insights-row">
                    <div className="insights-card">Market Pipeline</div>
                    <div className="insights-section-title">
                      <div className="metric-panel">
                        {[{
                          label: '$ Market Pipeline',
                          val: 85,
                          color: CANONICAL_SPECTRUM[15]
                        }, {
                          label: '$ Weighted Value',
                          val: 65,
                          color: CANONICAL_SPECTRUM[15]
                        }, {
                          label: '$ Total Approvals',
                          val: 45,
                          color: CANONICAL_SPECTRUM[15]
                        }, {
                          label: '$ Avg. Filing Fee',
                          val: 25,
                          color: CANONICAL_SPECTRUM[15]
                        }].map((item, idx) => <div key={idx} className="metric-panel__list">
                          <div className="metric-row">{item.label}</div>
                          <div className="metric-row__label">
                            <div style={{
                              width: `${item.val}%`,
                              ...getPbBarStyle(item.color)
                            }} className="metric-row__track"></div>
                          </div>
                        </div>)}
                      </div>
                    </div>
                  </div>

                  {/* Market Outcome Mix */}
                  <div className="report-card metric-row__fill">
                    <div className="insights-card">Market Outcome Mix</div>
                    <div className="insights-section-title">
                      <svg width="224" height="224" viewBox="0 0 224 224">
                        <circle cx="112" cy="112" r="96" fill="none" stroke="var(--border)" strokeWidth="32" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[8]} strokeWidth="32" strokeDasharray="341 603" strokeDashoffset="0" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[18]} strokeWidth="32" strokeDasharray="171 603" strokeDashoffset="-341" />
                        <circle cx="112" cy="112" r="96" fill="none" stroke={CANONICAL_SPECTRUM[28]} strokeWidth="32" strokeDasharray="90 603" strokeDashoffset="-512" />
                      </svg>
                      <div className="insights-chart-area insights-chart-area--md">
                        {[{
                          label: 'Approved',
                          color: CANONICAL_SPECTRUM[8]
                        }, {
                          label: 'Withdrawn',
                          color: CANONICAL_SPECTRUM[18]
                        }, {
                          label: 'Disapproved',
                          color: CANONICAL_SPECTRUM[28]
                        }].map((item, idx) => <div key={idx} className="insights-pie-legend">
                          <div style={{
                            ...getPbBarStyle(item.color)
                          }} className="insights-pie-legend__item"></div> {item.label}
                        </div>)}
                      </div>
                    </div>
                  </div>

                  {/* State Velocity Scorecard */}
                  <div className="report-card insights-pie-legend__swatch">
                    <div className="insights-card">State Velocity</div>
                    <div className="insights-section-title">
                      {[{
                        state: 'Georgia',
                        val: 75,
                        color: CANONICAL_SPECTRUM[20]
                      }, {
                        state: 'Ohio',
                        val: 60,
                        color: CANONICAL_SPECTRUM[20]
                      }, {
                        state: 'Michigan',
                        val: 45,
                        color: CANONICAL_SPECTRUM[20]
                      }, {
                        state: 'North Carolina',
                        val: 80,
                        color: CANONICAL_SPECTRUM[20]
                      }, {
                        state: 'Arizona',
                        val: 35,
                        color: CANONICAL_SPECTRUM[20]
                      }].map((item, idx) => <div key={idx} className="metric-bars">
                        <div className="metric-bars__row">{item.state}</div>
                        <div className="metric-bars__label">
                          <div style={{
                            width: `${item.val}%`,
                            ...getPbBarStyle(item.color)
                          }} className="metric-bars__track"></div>
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div>

                <div className="footer metric-bars__fill">
                  <span>Powered by <strong>www.thecomply.ai</strong></span>
                  <span>CONFIDENTIAL — Internal Corporate Use Only · All Filings Report</span>
                </div>
              </div>
            </div>
          </div>}

          {isCompetitors && <div className="report-outer report-outer--embedded insights-panel">
            <div className="report-scope">
              <div className="report-page report-outer--flush">
                <div className="kpi-banner-grid">
                  {kpiCardsResolved.length > 0 ? kpiCardsResolved.map((kpi, idx) => {
                    const cardProps = mapInsightsKpiToCardProps(kpi);
                    return (
                      <KPICard
                        key={`${cardProps.title}-${cardProps.value}-${idx}`}
                        {...cardProps}
                        staggerIndex={idx}
                      />
                    );
                  }) : <div className="insights-chart-area insights-chart-area--md">No data present</div>}
                </div>

                {/* Row 1: Geographical Intelligence Map & Rankings in ONE unified container (At the Top) */}
                <div
                  className="report-card volume-chart-panel geo-distribution-panel"
                  style={{ display: 'flex', flexDirection: 'column', width: '100%', marginBottom: '20px' }}
                >
                  <div className="chart-panel__header">
                    <h3 className="report-card-title">Geographical Distribution & State Rankings</h3>
                  </div>

                  <div className="chart-panel__body geo-distribution-panel__body">
                  <div className="geo-distribution-grid">
                    <div className="geo-distribution-map">
                      <div className="geo-distribution-map__inner">
                        <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
                          <Geographies geography="https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json">
                            {({ geographies }) => geographies.map((geo, geoIdx) => {
                                const stateName = geo.properties.name;
                                const stateCode = STATE_ABBREVIATIONS[stateName];
                                const count = resolveMapValueCount(currentMapData[stateCode]);
                                const variant = getGeoStateVariant(stateCode, count);
                                return (
                                  <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    className={`geo-map__state geo-map__state--${variant}`}
                                    stroke="var(--text2)"
                                    strokeWidth={0.75}
                                    style={{
                                      default: { outline: "none", transition: "all 0.2s", '--geo-state-delay': geoIdx },
                                      hover: { outline: "none", cursor: "pointer", opacity: 0.85 },
                                      pressed: { outline: "none" }
                                    }}
                                    onMouseEnter={(evt) => {
                                      setMapTooltip({
                                        x: evt.clientX,
                                        y: evt.clientY,
                                        content: getGeoStateTooltip(stateName, stateCode, count),
                                      });
                                    }}
                                    onMouseMove={(evt) => {
                                      setMapTooltip(prev => prev ? { ...prev, x: evt.clientX, y: evt.clientY } : null);
                                    }}
                                    onMouseLeave={() => {
                                      setMapTooltip(null);
                                    }}
                                  />
                                );
                              })
                            }
                          </Geographies>
                        </ComposableMap>
                      </div>
                    </div>

                    <div className="geo-distribution-rankings">
                      <h4 className="geo-rankings__heading">Active Region Rankings</h4>
                      <div className="geo-legend">
                        <div className="geo-legend__item">
                          <span className="geo-legend__swatch geo-legend__swatch--ours" aria-hidden="true" />
                          <span className="geo-legend__label">Our Filings</span>
                        </div>
                        <div className="geo-legend__item">
                          <span className="geo-legend__swatch geo-legend__swatch--other" aria-hidden="true" />
                          <span className="geo-legend__label">Competitor Filings</span>
                        </div>
                      </div>
                      <div
                        className="product-depth-chart region-ranking-chart"
                        style={{ '--geo-ranking-row-gap': `${geoRankingRowGap}px` }}
                      >
                        {resolvedRankingEntries.length === 0 ? (
                          <div style={{ color: 'var(--text2)', textAlign: 'center', padding: '40px 0' }}>No active states for this metric</div>
                        ) : (
                          resolvedRankingEntries.map((state, rankIdx) => {
                            const maxCount = resolvedRankingEntries[0]?.count || 1;
                            const hasOurs = state.subject > 0;
                            const totalWidth = Math.min(100, (state.count / maxCount) * 100);
                            const oursWidth = Math.min(100, ((state.subject || 0) / maxCount) * 100);

                            return (
                              <div key={`${state.state}_${state.count}_${state.subject || 0}`} className="product-depth-bar-row region-ranking-bar-row" style={{ '--rank-delay': rankIdx }}>
                                <div className="product-depth-bar-row__company region-ranking-bar-row__state">{state.state}</div>
                                <div className="product-depth-bar-row__track region-ranking-bar-row__track">
                                  {state.count > 0 && (
                                    <div
                                      className="product-depth-bar-row__fill geo-bar-fill--other"
                                      style={{ width: `${totalWidth}%` }}
                                    />
                                  )}
                                  {hasOurs && (
                                    <div
                                      className="product-depth-bar-row__fill geo-bar-fill--ours geo-bar-fill--overlay"
                                      style={{ width: `${oursWidth}%` }}
                                    />
                                  )}
                                </div>
                                <span className="product-depth-bar-row__value region-ranking-bar-row__value">
                                  {hasOurs ? (
                                    <>
                                      <span className="geo-bar-value--ours">{state.subject}</span>
                                      <span style={{ color: 'var(--text3)', margin: '0 2px', fontSize: '12px' }}>/</span>
                                      {state.count}
                                    </>
                                  ) : (
                                    state.count
                                  )}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Row 2: Volume & Cadence */}
                <div className="report-charts-row">
                  <div className="report-card volume-chart-panel" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="chart-panel__header">
                      <h3 className="report-card-title">Filing Volume by Company & Type</h3>
                      {competitorVolumeDataResolved?.datasets?.length ? (
                        <div className="product-depth-legend" aria-label="Chart legend">
                          {(competitorVolumeDataResolved.datasets || []).map((ds) => (
                            <div key={ds.label} className="product-depth-legend__item">
                              <span
                                className="product-depth-legend__swatch"
                                style={{ background: getFilingVolumeTypeColor(ds.label) }}
                                aria-hidden="true"
                              />
                              <span className="product-depth-legend__label">{ds.label}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="chart-panel__body">
                      {competitorVolumeDataResolved?.labels?.length ? (
                        <CompetitorVolumeChart
                          data={{
                            ...competitorVolumeDataResolved,
                            datasets: (competitorVolumeDataResolved.datasets || [])
                              .map(ds => ({
                                ...ds,
                                backgroundColor: getFilingVolumeTypeColor(ds.label),
                              })),
                          }}
                          subjectCompany={TOP_COMPANY}
                          setTooltip={setMapTooltip}
                        />
                      ) : (
                        <div className="insights-chart-area insights-chart-area--md">No data present</div>
                      )}
                    </div>
                  </div>

                  <div className="report-card volume-chart-panel" style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="chart-panel__header">
                      <h3 className="report-card-title">Weekly Filing Cadence</h3>
                    </div>
                    <div className="chart-panel__body">
                      {competitorCadenceDataResolved.length > 0 ? (
                        <CompetitorCadenceHeatmap
                          data={competitorCadenceDataResolved.map(r => ({ ...r, color: COMPETITOR_CADENCE_COLOR }))}
                          weeks={competitorCadenceWeeksResolved.length > 0 ? competitorCadenceWeeksResolved : undefined}
                          setTooltip={setMapTooltip}
                        />
                      ) : (
                        <div className="insights-chart-area insights-chart-area--md">No data present</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 3: Product Category Depth — life, annuity, medicare, health */}
                <div className="report-card product-depth-panel">
                  <div className="chart-panel__header">
                    <h3 className="report-card-title">Product Category Depth</h3>
                    <div className="product-depth-legend" aria-label="Chart legend">
                      <div className="product-depth-legend__item">
                        <span className="product-depth-legend__swatch product-depth-legend__swatch--ours" aria-hidden="true" />
                        <span className="product-depth-legend__label">Our Filings</span>
                      </div>
                      <div className="product-depth-legend__item">
                        <span className="product-depth-legend__swatch product-depth-legend__swatch--competitor" aria-hidden="true" />
                        <span className="product-depth-legend__label">Competitor Filings</span>
                      </div>
                    </div>
                  </div>
                  <div className="product-depth-grid product-depth-grid--quad">
                    {productDepthColumns.map((column) => (
                      <div key={column.title} className="product-depth-column">
                        <h4 className="product-depth-column__title">{column.title}</h4>
                        {hasRenderableProductData(column.data) ? (
                          <ProductCategoryDepthChart
                            categories={column.data.categories}
                            categoryDetails={column.data.categoryDetails}
                            dataByCompany={column.data.dataByCompany}
                            maxVal={column.data.maxVal}
                            companyOrder={column.data.companyOrder}
                            subjectCompany={TOP_COMPANY}
                          />
                        ) : (
                          <div className="page-empty page-empty--compact">No data present</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>}
        </div> : <div className="table-area insights-pie-legend__item">
          <div className="insights-pie-legend__swatch">
            <h2 className="insights-card">{activePageTab}</h2>
            <p className="insights-section-title">Coming Soon...</p>
          </div>
        </div>}


      <FiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filterState={filterState}
        setFilterState={setFilterState}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterType={filterType}
        setFilterType={setFilterType}
        filterToi={filterToi}
        setFilterToi={setFilterToi}
        filterCompany={filterCompany}
        setFilterCompany={setFilterCompany}
        options={options}
        statuses={statuses}
        tois={drawerTois}
        isOurFilings={isOurFilings}
        isCompetitors={isCompetitors}
        mappedCompetitors={mappedCompetitors}
        setCurrentPage={setCurrentPage}
        isInsights={activePageTab === 'Insights'}
        subStart={subStart}
        setSubStart={setSubStart}
        subEnd={subEnd}
        setSubEnd={setSubEnd}
        tempSubRange={tempSubRange}
        setTempSubRange={setTempSubRange}
        dispStart={dispStart}
        setDispStart={setDispStart}
        dispEnd={dispEnd}
        setDispEnd={setDispEnd}
        tempDispRange={tempDispRange}
        setTempDispRange={setTempDispRange}
        submissionDateTitle="Submission dates"
        dispositionDateTitle="Disposition dates"
      />

      {mapTooltip && (
        <div
          className="map-hover-tooltip"
          style={{
            top: mapTooltip.y + 15,
            left: mapTooltip.x + 15
          }}
        >
          {mapTooltip.content}
        </div>
      )}
    </div>
  </>;
};
export default Filings;
